import { ILabels, INormalizedTournamentData, IStatistic } from '../../../../types';
import { SMPContainer, SMPFlex, SMPRedCardsIcon, SMPText, SMPYellowCardsIcon } from '../../../index';
import { YELLOW_CARDS, RED_CARDS } from '../../../../constants/statistics';
import { snakeToCamelCase, camelToSnakeCase, toSentenceCase } from '@sportal365/smp-widget-utils';
import { useMemo, useState } from 'react';
import useResizeObserver from '../../../../hooks/useResizeObserver';
import { MAX_MOBILE_WIDTH } from '../../../../constants/generic';

export interface IProfileSeasonStatisticsProps {
  season?: { id: string; name: string; status?: string; tournament?: INormalizedTournamentData };
  statistics?: IStatistic[];
  tournamentName?: string;
  labels?: ILabels;
}

export const ProfileSeasonStatistics = ({ season, statistics, tournamentName, labels }: IProfileSeasonStatisticsProps): JSX.Element => {
  const [isMobile, setIsMobile] = useState(false);
  const measuredRef = useResizeObserver((fieldElement) => {
    setIsMobile(fieldElement.offsetWidth < MAX_MOBILE_WIDTH);
  });

  const styles = useMemo(() => {
    return {
      display: 'grid',
      gridGap: isMobile ? '40px' : '40px 85px',
      gridRowGap: '40px',
      gridTemplateColumns: 'repeat(auto-fit, minmax(100px, 1fr))',
      paddingTop: '20px',
      paddingBottom: '30px',
      paddingInline: isMobile ? '16px' : '32px',
    };
  }, [isMobile]);

  return (
    <>
      <SMPFlex
        ref={measuredRef}
        css={{
          mt: '$3',
          backgroundColor: '$rowBackgroundColor',
          justifyContent: 'flex-start',
          py: '$2',
          '@bp0': { px: '16px' },
          '@bp1': { px: '32px' },
        }}
      >
        <SMPText variant='contrast' css={{ marginRight: '5px', '@bp0': { fontSize: '12px' }, '@bp2': { fontSize: '14px' } }}>
          {tournamentName ? tournamentName : season?.tournament?.name?.toUpperCase()}
        </SMPText>
        <SMPText variant='contrast' css={{ '@bp0': { fontSize: '12px' }, '@bp2': { fontSize: '14px' } }}>
          {season?.name}
        </SMPText>
      </SMPFlex>
      <SMPContainer style={styles}>
        {statistics?.map((statistic) => {
          const camelStatName = snakeToCamelCase(statistic.name ?? '', true);
          const snakeStatName = camelToSnakeCase(camelStatName);

          const label = labels?.[snakeStatName] ?? toSentenceCase(snakeStatName);

          return (
            <SMPFlex variant='column' key={statistic?.name}>
              <SMPFlex css={{ justifyContent: 'left', alignItems: 'center' }}>
                {statistic.key === YELLOW_CARDS && <SMPYellowCardsIcon style={{ width: '20px', height: '20px', marginTop: '3px' }} />}
                {statistic.key === RED_CARDS && <SMPRedCardsIcon style={{ width: '20px', height: '20px', marginTop: '3px' }} />}
                <SMPText size='6' variant='contrast' css={{ fontWeight: 'bolder' }}>
                  {statistic?.value}
                </SMPText>
              </SMPFlex>
              <SMPText css={{ fontWeight: 'lighter', mt: '$2' }} variant='contrast' size='3'>
                {label}
              </SMPText>
            </SMPFlex>
          );
        })}
      </SMPContainer>
    </>
  );
};
